import React, { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayPanel } from 'primereact/overlaypanel';
import Button from '../../../../generic-components/Button';
import { ChartIndex } from './ChartIndex';
import { ChartSummaryData } from '../../types/ChartSummaryData';
import { formatValueWithoutCurrency } from '../../../../helpers/currency.helper';
import { IChartFilter } from '../../contexts/DashboardContext';
import { mapOptionsForPeriodTypes } from '../../utils/insights.utils';
import { TotalPairView } from '../TotalPairView';
import { ReactComponent as DownArrowIconTransparent } from '../../../../assets/svg/shared/icon-dropdown-arrow-transparent.svg';

export enum ProfileInsightsStates {
  Yearly = 'Year',
  Monthly = 'Month',
  Quarterly = 'Quarter',
}

export interface InsightsWidgetHeaderProps {
  title: string;
  onStateChange: (state: ProfileInsightsStates, option: string) => void;
  showChartIndex?: boolean;
  chartSummaryData: ChartSummaryData;
  chartFilters?: IChartFilter;
  periodType?: string;
  subPeriodType?: string;
  isMonthlyPeriodDisabled?: boolean;
}

export const InsightsWidgetHeader = ({
  title,
  showChartIndex,
  onStateChange,
  chartSummaryData,
  chartFilters,
  periodType,
  subPeriodType,
  isMonthlyPeriodDisabled,
}: InsightsWidgetHeaderProps) => {
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const overlayPanel = useRef<any>(null);
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>(periodType || ProfileInsightsStates.Yearly);
  const [draftPeriodType, setDraftPeriodType] = useState<string>(periodType || ProfileInsightsStates.Yearly);
  const [selectedSubPeriodType, setSelectedSubPeriodType] = useState<string>(
    subPeriodType || new Date().getFullYear().toString(),
  );
  const mappedFilterOptions: {
    [key: string]: string[];
  } = useMemo(() => mapOptionsForPeriodTypes(chartFilters), [chartFilters]);
  const filterMainMenuOptions = Object.keys(mappedFilterOptions);
  const activeSubPeriodTypeOptions = mappedFilterOptions?.[draftPeriodType];

  const toggleOverlay = (e: any) => {
    overlayPanel?.current?.toggle(e);
  };

  const handleStateChange = (state: string) => {
    setDraftPeriodType(state);
  };

  const handleSubPeriodTypeChange = (subPeriodTypeOption: string) => {
    setSelectedSubPeriodType(subPeriodTypeOption);
    setSelectedPeriodType(draftPeriodType);

    setTimeout(() => {
      onStateChange(draftPeriodType as ProfileInsightsStates, subPeriodTypeOption);
    }, 0);

    toggleOverlay({});
  };
  const renderPeriodTypesList = () => {
    const disableButton = (item: string) => isMonthlyPeriodDisabled && item === ProfileInsightsStates.Monthly;
    const disabledClass = (item: string) => (disableButton(item) ? 'text-bw03' : 'hover:font-metricRMedium');

    return filterMainMenuOptions?.map((item) => (
      <li className={`p-2 ${draftPeriodType === item && 'bg-action00'}`} key={`li_${item}`}>
        <Button
          className={disabledClass(item)}
          id={`'set${item}`}
          onClick={() => handleStateChange(item)}
          childElement={item}
          key={`button_${item}`}
          testId={`insightsWidgetHeader__period-type-item__${item}`}
          disable={disableButton(item)}
        />
      </li>
    ));
  };
  const getPeriodTrendText = () => `${t('INSIGHT_WIDGET.FROM_PREVIOUS')} ${periodType?.toLowerCase()}`;
  return (
    <div data-testid='insightsWidgetHeader__wrapper'>
      <div className='flex justify-between'>
        <div className='flex flex-col font-metricR'>
          <h2 className='font-metricRSemibold text-bw08 text-[14px]' data-testid='insightsWidgetHeader__title'>
            {title}
          </h2>
          <TotalPairView
            title=''
            value={formatValueWithoutCurrency(chartSummaryData?.total || 0, 2)}
            testId='insightsWidgetHeader__amount'
          />
        </div>

        <div className='flex justify-start'>
          <div className='flex items-start h-[21px]'>
            <Button
              className='h-full flex items-center cursor-pointer'
              id='paginationDown'
              testId='insightsWidgetHeader__filters-dropdown-button'
              onClick={toggleOverlay}
              childElement={
                <div className='flex'>
                  <p className='font-metricR text-bw08 text-[14px] mr-[10px]'>{selectedPeriodType}</p>
                  <p className='font-metricR text-bw08 text-[14px] mr-[5px]'>{selectedSubPeriodType}</p>
                  <DownArrowIconTransparent className='stroke-action03 w-[16px] h-[16px] cursor-pointer' />
                </div>
              }
              key={`InsightFiltersBtn__${Math.random()}`}
            />
          </div>

          <OverlayPanel ref={overlayPanel}>
            <div className='flex'>
              <div className='flex flex-col p-2'>
                <ul className='font-metricR text-[14px] w-[90px]'>{renderPeriodTypesList()}</ul>
              </div>
              <div className='flex flex-col border-[1px] border-bw03 rounded-[4px] ml-3 w-[80px]'>
                <ul
                  className='flex flex-col font-metricR text-[14px] w-full p-2'
                  style={{ height: '250px', overflowY: 'scroll' }}
                >
                  {activeSubPeriodTypeOptions?.map((subPeriodTypeOption: string) => (
                    <div key={subPeriodTypeOption}>
                      <li
                        className={`flex w-full items-center justify-center h-[36px] ${selectedSubPeriodType === subPeriodTypeOption ? 'bg-action00' : ''}`}
                        key={`li_${subPeriodTypeOption}`}
                      >
                        <Button
                          key={`subMenuOption_${subPeriodTypeOption}`}
                          className='w-full flex items-center justify-center hover:font-metricRMedium'
                          onClick={() => handleSubPeriodTypeChange(subPeriodTypeOption)}
                          id={`setSubOption_${subPeriodTypeOption}`}
                          childElement={<div className=''>{subPeriodTypeOption}</div>}
                          testId={`insightsWidgetHeader__sub-period-type-item__${subPeriodTypeOption}`}
                        />
                      </li>
                    </div>
                  ))}
                </ul>
              </div>
            </div>
          </OverlayPanel>
        </div>
      </div>
      {chartSummaryData && chartSummaryData.total !== 0 && (
        <div className='flex justify-between mb-2 '>
          {/* // bring back presentation logic when bugs are fixed */}
          <div
            className={` ${chartSummaryData.diff ? 'invisible' : 'invisible'} text-[12px]`}
            data-testid='insightsWidgetHeader__trend-text'
          >
            <span className={`${chartSummaryData.positiveTrend ? 'text-badgeGreen03' : 'text-red02'}`}>
              <span data-testid='insightsWidgetHeader__trend-direction'>
                {chartSummaryData.incrementSinceLastPeriod ? 'Up' : 'Down'}
              </span>
              {' '}
              <span data-testid='insightsWidgetHeader__trend-diff'>{chartSummaryData.diff}</span>
              {' '}
            </span>
            {' '}
            <span className='text-bw08 text-[12px] font-metricR'>{getPeriodTrendText()}</span>
          </div>
          {showChartIndex && <ChartIndex />}
        </div>
      )}
    </div>
  );
};
